import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePromptBuffer, { PROMPT_BUFFER_SCOPES } from './usePromptBuffer';

const ATTACHMENT_WARNING_MESSAGE =
  'Attachments from your previous draft were not restored automatically. Please re-attach them before sending.';

const emptyArray = [];

export default function usePromptDraftController({
  isRefinement = false,
  aiStrategyId = null,
  revisionId = null,
} = {}) {
  const bufferScope = isRefinement ? PROMPT_BUFFER_SCOPES.REFINE : PROMPT_BUFFER_SCOPES.GLOBAL;

  const bufferConfig = useMemo(
    () => ({
      scope: bufferScope,
      strategyId: bufferScope === PROMPT_BUFFER_SCOPES.REFINE ? aiStrategyId || null : null,
      revisionId: bufferScope === PROMPT_BUFFER_SCOPES.REFINE ? revisionId || null : null,
    }),
    [aiStrategyId, bufferScope, revisionId],
  );

  const { draft, saveDraft, clearDraft } = usePromptBuffer(bufferConfig);

  const [promptValue, setPromptValueState] = useState('');
  const [restoredAttachmentWarning, setRestoredAttachmentWarning] = useState(null);

  const promptValueRef = useRef('');
  const attachmentsMetaRef = useRef(emptyArray);
  const hasUnrestoredAttachmentsRef = useRef(false);
  // Track whether attachments have been actively registered (user added files in this session)
  const attachmentsActivelyRegisteredRef = useRef(false);

  useEffect(() => {
    const nextPrompt = draft?.value || '';
    setPromptValueState((current) => (current === nextPrompt ? current : nextPrompt));
    promptValueRef.current = nextPrompt;

    const savedAttachments = Array.isArray(draft?.attachmentsMeta) ? draft.attachmentsMeta : emptyArray;
    attachmentsMetaRef.current = savedAttachments;

    // Only show warning if:
    // 1. There are saved attachments in the draft AND
    // 2. User hasn't actively added attachments in this session
    // This prevents the warning from re-appearing after user adds files
    const shouldWarn = savedAttachments.length > 0 && !attachmentsActivelyRegisteredRef.current;
    hasUnrestoredAttachmentsRef.current = shouldWarn;
    setRestoredAttachmentWarning(shouldWarn ? ATTACHMENT_WARNING_MESSAGE : null);
  }, [draft]);

  const setPromptValue = useCallback(
    (valueOrUpdater) => {
      setPromptValueState((previous) => {
        const nextValueRaw =
          typeof valueOrUpdater === 'function' ? valueOrUpdater(previous) : valueOrUpdater;
        const nextValue = typeof nextValueRaw === 'string' ? nextValueRaw : '';

        if (nextValue === previous) {
          return previous;
        }

        promptValueRef.current = nextValue;
        saveDraft(nextValue, attachmentsMetaRef.current);

        return nextValue;
      });
    },
    [saveDraft],
  );

  const clearPromptValue = useCallback(() => {
    setPromptValueState('');
    promptValueRef.current = '';
    attachmentsMetaRef.current = emptyArray;
    hasUnrestoredAttachmentsRef.current = false;
    attachmentsActivelyRegisteredRef.current = false;
    setRestoredAttachmentWarning(null);
    clearDraft();
  }, [clearDraft]);

  const registerAttachmentsMeta = useCallback(
    (meta = emptyArray) => {
      const normalized = Array.isArray(meta)
        ? meta
            .filter(Boolean)
            .map((item) => ({
              id: item.id ?? null,
              name: typeof item.name === 'string' ? item.name : '',
              size: typeof item.size === 'number' && Number.isFinite(item.size) ? item.size : 0,
              type: typeof item.type === 'string' ? item.type : '',
            }))
        : emptyArray;

      attachmentsMetaRef.current = normalized;

      if (normalized.length > 0) {
        // Mark that user has actively added attachments in this session
        // This prevents the warning from re-appearing when draft updates
        attachmentsActivelyRegisteredRef.current = true;
        hasUnrestoredAttachmentsRef.current = false;
        setRestoredAttachmentWarning(null);
      } else if (hasUnrestoredAttachmentsRef.current) {
        setRestoredAttachmentWarning(ATTACHMENT_WARNING_MESSAGE);
      } else {
        setRestoredAttachmentWarning(null);
      }

      saveDraft(promptValueRef.current, normalized);
    },
    [saveDraft],
  );

  return {
    promptValue,
    setPromptValue,
    clearPromptValue,
    restoredAttachmentWarning,
    registerAttachmentsMeta,
  };
}

